import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveInvestigations = () => {
  const [selectedTab, setSelectedTab] = useState('active');
  const [sortBy, setSortBy] = useState('updated');

  const investigations = [
    {
      id: 'inv_001',
      title: 'EU Climate Policy Disinformation Campaign',
      description: 'Coordinated network spreading false claims about Green Deal economic impact across multiple platforms',
      status: 'active',
      priority: 'high',
      assignedTo: ['Dr. Maria Schmidt', 'Jean Dubois'],
      leadInvestigator: 'Dr. Maria Schmidt',
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000),
      deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      progress: 65,
      evidenceCount: 47,
      sourcesVerified: 23,
      platformsAffected: ['Facebook', 'Twitter', 'TikTok'],
      regions: ['Western Europe', 'Central Europe'],
      tags: ['climate', 'policy', 'coordinated'],
      confidenceLevel: 0.82,
      riskLevel: 'high'
    },
    {
      id: 'inv_002',
      title: 'Deepfake Detection: Political Figure Impersonation',
      description: 'AI-generated video content featuring fabricated statements from EU officials',
      status: 'peer-review',
      priority: 'critical',
      assignedTo: ['Prof. Alessandro Rossi', 'Dr. Anna Kowalski'],
      leadInvestigator: 'Prof. Alessandro Rossi',
      startDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000),
      deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      progress: 85,
      evidenceCount: 31,
      sourcesVerified: 28,
      platformsAffected: ['YouTube', 'Twitter', 'Telegram'],
      regions: ['Multi-regional'],
      tags: ['deepfake', 'politics', 'AI-generated'],
      confidenceLevel: 0.94,
      riskLevel: 'critical'
    },
    {
      id: 'inv_003',
      title: 'Healthcare Misinformation Network Analysis',
      description: 'Systematic analysis of anti-vaccination content distribution patterns',
      status: 'active',
      priority: 'medium',
      assignedTo: ['Dr. Sophie Laurent', 'Dr. Hans Mueller'],
      leadInvestigator: 'Dr. Sophie Laurent',
      startDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
      lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000),
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      progress: 40,
      evidenceCount: 89,
      sourcesVerified: 34,
      platformsAffected: ['WhatsApp', 'Telegram', 'Facebook'],
      regions: ['Northern Europe', 'Western Europe'],
      tags: ['health', 'vaccination', 'network-analysis'],
      confidenceLevel: 0.71,
      riskLevel: 'medium'
    },
    {
      id: 'inv_004',
      title: 'Election Integrity Claims Investigation',
      description: 'Fact-checking claims about voting system vulnerabilities in upcoming elections',
      status: 'draft',
      priority: 'high',
      assignedTo: ['Dr. Elena Popov', 'Dr. Carlos Mendez'],
      leadInvestigator: 'Dr. Elena Popov',
      startDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      lastUpdated: new Date(Date.now() - 8 * 60 * 60 * 1000),
      deadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      progress: 20,
      evidenceCount: 15,
      sourcesVerified: 8,
      platformsAffected: ['Twitter', 'Facebook', 'Reddit'],
      regions: ['Eastern Europe'],
      tags: ['elections', 'voting', 'integrity'],
      confidenceLevel: 0.58,
      riskLevel: 'high'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'peer-review': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'draft': return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'on-hold': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  const formatDeadline = (date) => {
    const now = new Date();
    const diffInDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) {
      return 'Overdue';
    } else if (diffInDays === 0) {
      return 'Due today';
    } else if (diffInDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffInDays} days`;
    }
  };

  const filteredInvestigations = investigations?.filter(inv => {
    switch (selectedTab) {
      case 'active': return inv?.status === 'active';
      case 'review': return inv?.status === 'peer-review';
      case 'draft': return inv?.status === 'draft';
      case 'all': return true;
      default: return true;
    }
  });

  const sortedInvestigations = [...filteredInvestigations]?.sort((a, b) => {
    switch (sortBy) {
      case 'updated':
        return b?.lastUpdated - a?.lastUpdated;
      case 'deadline':
        return a?.deadline - b?.deadline;
      case 'priority':
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
      case 'progress':
        return b?.progress - a?.progress;
      default:
        return 0;
    }
  });

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-purple-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Active Investigations</h3>
              <p className="text-sm text-muted-foreground">Ongoing fact-checking and research projects</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Plus">
              New Investigation
            </Button>
            <Button variant="outline" size="sm" iconName="Filter">
              Filter
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            {[
              { id: 'active', label: 'Active', count: investigations?.filter(i => i?.status === 'active')?.length },
              { id: 'review', label: 'Peer Review', count: investigations?.filter(i => i?.status === 'peer-review')?.length },
              { id: 'draft', label: 'Draft', count: investigations?.filter(i => i?.status === 'draft')?.length },
              { id: 'all', label: 'All', count: investigations?.length }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setSelectedTab(tab?.id)}
                className={`
                  px-3 py-2 text-sm font-medium rounded-lg transition-smooth
                  ${selectedTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                {tab?.label} ({tab?.count})
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="updated">Sort by Last Updated</option>
            <option value="deadline">Sort by Deadline</option>
            <option value="priority">Sort by Priority</option>
            <option value="progress">Sort by Progress</option>
          </select>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {sortedInvestigations?.map((investigation) => (
            <div
              key={investigation?.id}
              className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(investigation?.status)}`}>
                      {investigation?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                    <Icon name="AlertTriangle" size={14} className={getPriorityColor(investigation?.priority)} />
                    <span className={`text-xs font-medium ${getPriorityColor(investigation?.priority)}`}>
                      {investigation?.priority?.toUpperCase()}
                    </span>
                    <div className={`w-2 h-2 ${getRiskColor(investigation?.riskLevel)} rounded-full`} />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">{investigation?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{investigation?.description}</p>
                </div>
                <Button variant="ghost" size="icon" iconName="MoreHorizontal" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <div className="text-xs text-muted-foreground">Progress</div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${investigation?.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground">{investigation?.progress}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Evidence</div>
                  <div className="text-sm font-medium text-foreground">{investigation?.evidenceCount} items</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Verified Sources</div>
                  <div className="text-sm font-medium text-foreground">{investigation?.sourcesVerified}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Confidence</div>
                  <div className="text-sm font-medium text-foreground">{(investigation?.confidenceLevel * 100)?.toFixed(0)}%</div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-4">
                  <span className="text-xs text-muted-foreground">
                    <Icon name="User" size={12} className="inline mr-1" />
                    Lead: {investigation?.leadInvestigator}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} className="inline mr-1" />
                    {formatTimeAgo(investigation?.lastUpdated)}
                  </span>
                  <span className={`text-xs font-medium ${formatDeadline(investigation?.deadline)?.includes('Overdue') ? 'text-red-600' : 'text-muted-foreground'}`}>
                    <Icon name="Calendar" size={12} className="inline mr-1" />
                    {formatDeadline(investigation?.deadline)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {investigation?.tags?.slice(0, 3)?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                  {investigation?.tags?.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full">
                      +{investigation?.tags?.length - 3}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                  <Button variant="default" size="sm">
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActiveInvestigations;